import { useCallback, useEffect, useState } from 'react';
import type { Panel } from './components/Shell';

/**
 * Adresy ekranów.
 *
 * Do tej pory cała aplikacja stała pod jednym adresem: gdziekolwiek się było, w pasku
 * przeglądarki widniało `app.firmeto.pl`. Znaczyło to trzy rzeczy naraz — „wstecz" wyrzucało
 * z aplikacji zamiast cofać o ekran, odświeżenie strony wracało na początek, a linku do faktur
 * nie dało się ani zapisać w zakładkach, ani wysłać księgowej.
 *
 * Adresy są po polsku, tak jak `/rejestracja` i `/zaproszenie`, które były tu pierwsze.
 * Nie tłumaczymy ich na cztery języki: adres jest identyfikatorem, a nie treścią — link
 * przetłumaczony przestałby działać u odbiorcy, który czyta aplikację w innym języku.
 *
 * Router jest własny, bo tras jest sześć i wszystkie dane i tak płyną przez właściwości.
 * `react-router` znaczyłby tu przebudowę na `<Routes>` i `<Outlet>` oraz kilkadziesiąt kilobajtów
 * w paczce, która i tak przekracza próg ostrzeżenia.
 */

export type Route =
  | { screen: 'start' }
  | { screen: 'register' }
  | { screen: 'invitation'; token: string }
  | { screen: 'companies' }
  | { screen: 'company'; companyId: string; panel: Panel }
  | { screen: 'office' };

/**
 * Nazwa ekranu w adresie. `Record<Panel, string>` jest tu celowo — dopisanie ekranu bez adresu
 * nie przejdzie kompilacji, więc nie da się dodać miejsca, do którego nie prowadzi żaden link.
 */
const PANEL_PATHS: Record<Panel, string> = {
  dashboard: 'pulpit',
  invoices: 'faktury',
  settlements: 'rozrachunki',
  counterparties: 'kontrahenci',
  products: 'towary',
  costs: 'koszty',
  documents: 'dokumenty',
  mailboxConfig: 'dokumenty/konfiguracja-skrzynki',
  chart: 'plan-kont',
  journal: 'dziennik',
  reports: 'sprawozdania',
  ledger: 'ksiega',
  ryczalt: 'ewidencja',
  assets: 'srodki-trwale',
  inventory: 'remanent',
  periods: 'okresy',
  annual: 'sprawozdanie-roczne',
  billing: 'oplaty',
  settings: 'ustawienia',
  access: 'dostep',
  numbering: 'numeracja',
  bank: 'rachunki',
};

const PANEL_BY_PATH = new Map<string, Panel>(
  Object.entries(PANEL_PATHS).map(([panel, path]) => [path, panel as Panel]),
);

/** Adresy obsługiwane przez warstwę logowania — nie są ekranami i nie przechodzą przez router. */
export const AUTH_PATHS = { callback: '/logowanie/powrot', silentRenew: '/logowanie/odnowienie' };

export function isAuthPath(pathname: string): boolean {
  return Object.values(AUTH_PATHS).includes(pathname);
}

export function parsePath(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean).map(decodeURIComponent);
  const [first, second] = parts;

  if (first === 'rejestracja') return { screen: 'register' };
  // Token zaproszenia bywa długi i bywa w nim ukośnik — bierzemy całą resztę adresu
  if (first === 'zaproszenie' && second) return { screen: 'invitation', token: parts.slice(1).join('/') };
  if (first === 'biuro') return { screen: 'office' };
  if (first === 'firmy') {
    return second ? { screen: 'company', companyId: second, panel: panelFromPath(parts.slice(2).join('/')) } : { screen: 'companies' };
  }
  // Nieznany adres prowadzi na wejście: gość zobaczy stronę startową, zalogowany swoje firmy
  return { screen: 'start' };
}

export function pathOf(route: Route): string {
  switch (route.screen) {
    case 'start':
      return '/';
    case 'register':
      return '/rejestracja';
    case 'invitation':
      return `/zaproszenie/${route.token}`;
    case 'companies':
      return '/firmy';
    case 'company':
      return `/firmy/${route.companyId}/${PANEL_PATHS[route.panel]}`;
    case 'office':
      return '/biuro';
  }
}

/** Nieznany ekran firmy to pulpit, a nie pusta strona — link z literówką ma dokądś prowadzić. */
function panelFromPath(path: string | undefined): Panel {
  return (path && PANEL_BY_PATH.get(path)) || 'dashboard';
}

export type Navigate = (next: Route, options?: { replace?: boolean }) => void;

/**
 * Bieżący adres i przejście pod inny.
 *
 * `popstate` jest tu po to, żeby „wstecz" i „dalej" w przeglądarce naprawdę cofały ekran.
 * Bez tego nasłuchu adres zmieniałby się w pasku, ale widok zostawałby ten sam — czyli gorzej
 * niż przed zmianą, bo pasek zacząłby kłamać.
 */
export function useRoute(): [Route, Navigate] {
  const [route, setRoute] = useState<Route>(() => parsePath(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(parsePath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /*
   * Adres doprowadzony do postaci, którą sami byśmy złożyli: `/firmy/abc` staje się
   * `/firmy/abc/pulpit`, a adres z literówki — stroną startową. Chodzi o to, żeby skopiowany
   * z paska link prowadził dokładnie tam, gdzie się stoi.
   *
   * Adresów logowania to nie dotyczy: niosą kod z Keycloaka, a porządkowanie skasowałoby go,
   * zanim zdążyłby go odczytać powrót z logowania.
   */
  useEffect(() => {
    if (isAuthPath(window.location.pathname)) return;
    const canonical = pathOf(parsePath(window.location.pathname));
    if (canonical !== window.location.pathname) window.history.replaceState({}, '', canonical);
  }, []);

  const navigate = useCallback<Navigate>((next, options) => {
    const path = pathOf(next);
    // Wejście tam, gdzie się już jest, nie ma zostawiać wpisu w historii — inaczej „wstecz"
    // trzeba by klikać tyle razy, ile razy ktoś kliknął w tę samą pozycję nawigacji
    if (options?.replace || path === window.location.pathname) {
      window.history.replaceState({}, '', path);
    } else {
      window.history.pushState({}, '', path);
    }
    setRoute(next);
  }, []);

  return [route, navigate];
}
